import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateReport } from '../../../store/slices/gameTestingSlice';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/config';
import BugReportForm from './BugReportForm';
import gameNames from '../data/gameNames';
import './EditGameReviewModal.scss';

const EditGameReviewModal = ({ show, onHide, report }) => {
  const dispatch = useDispatch();
  const { selectedReport } = useSelector(state => state.gameTesting);
  const [formData, setFormData] = useState({
    overallExperience: '',
    levelsFeedback: '',
    tutorialFeedback: '',
    positiveAspects: '',
    improvementSuggestions: '',
    selectedGames: [],
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showBugReportForm, setShowBugReportForm] = useState(false);
  const [bugSubmitSuccess, setBugSubmitSuccess] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [formInitialized, setFormInitialized] = useState(false);
  const fileInputRef = useRef(null);
  
  // Use selectedReport from Redux store if available (updated after bug addition)
  const currentReport = selectedReport && selectedReport.id === report?.id ? selectedReport : report;

  // Initialize form data when component loads or report changes
  useEffect(() => {
    if (currentReport && !formInitialized) {
      setFormData({
        overallExperience: currentReport.overallExperience || '',
        levelsFeedback: currentReport.levelsFeedback || '',
        tutorialFeedback: currentReport.tutorialFeedback || '',
        positiveAspects: currentReport.positiveAspects || '',
        improvementSuggestions: currentReport.improvementSuggestions || '',
        selectedGames: currentReport.selectedGames || [],
        images: currentReport.images || []
      });
      setFormInitialized(true);
    }
  }, [currentReport, formInitialized]);

  // Only update selectedGames from currentReport if it's different from formData
  useEffect(() => {
    if (currentReport && formInitialized && currentReport.selectedGames) {
      const currentGames = JSON.stringify(currentReport.selectedGames);
      const formGames = JSON.stringify(formData.selectedGames);
      
      if (currentGames !== formGames) {
        setFormData(prev => ({
          ...prev,
          selectedGames: currentReport.selectedGames
        }));
      }
    }
  }, [currentReport?.selectedGames, formData.selectedGames, formInitialized, currentReport]);

  // Reset form when modal is closed and reopened
  useEffect(() => {
    if (!show) {
      setFormInitialized(false);
    }
  }, [show]);

  if (!show || !currentReport) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGameSelection = async (game) => {
    const newSelectedGames = formData.selectedGames.includes(game)
      ? formData.selectedGames.filter(g => g !== game)
      : [...formData.selectedGames, game];
    
    // Update local state immediately
    setFormData(prev => ({
      ...prev,
      selectedGames: newSelectedGames
    }));
    
    // Auto-save the game selection to database to prevent loss
    setAutoSaving(true);
    try {
      await dispatch(updateReport({
        reportId: currentReport.id,
        reportData: {
          selectedGames: newSelectedGames
        }
      }));
      // Brief success indicator
      setTimeout(() => setAutoSaving(false), 1000);
    } catch (err) {
      console.error('Failed to auto-save game selection:', err);
      setAutoSaving(false);
      // Don't show error to user for auto-save, just log it
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    const uploadedImages = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `game-review-${currentReport.id}-${Date.now()}-${i}`;
        const storageRef = ref(storage, `game-testing/${currentReport.id}/images/${fileName}`);
        
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        // Track upload progress for this file
        uploadTask.on('state_changed', 
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [fileName]: progress
            }));
          }
        );
        
        // Wait for upload to complete
        await uploadTask;
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        
        uploadedImages.push({
          url: downloadURL,
          name: file.name,
          uploadedAt: new Date().toISOString()
        });
        
        // Remove from progress tracking
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileName];
          return newProgress;
        });
      }
      
      // Update form data with new images
      const updatedImages = [...formData.images, ...uploadedImages];
      setFormData(prev => ({
        ...prev,
        images: updatedImages
      }));
      
      // Auto-save images to database - use a direct approach to avoid file combination logic
      await dispatch(updateReport({
        reportId: currentReport.id,
        reportData: {
          // Don't include images here as they will be overridden by the allFiles logic
        },
        files: [], // Pass empty files array since we're handling upload ourselves
        directImages: updatedImages // Pass images separately to bypass the combination logic
      })).unwrap();
      
    } catch (err) {
      console.error('Failed to upload images:', err);
      setError('Failed to upload some images. Please try again.');
    } finally {
      setUploadingImages(false);
      setUploadProgress({});
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleImageUpload(files);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = async (imageIndex) => {
    const updatedImages = formData.images.filter((_, index) => index !== imageIndex);
    setFormData(prev => ({
      ...prev,
      images: updatedImages
    }));
    
    // Auto-save to database
    try {
      await dispatch(updateReport({
        reportId: currentReport.id,
        reportData: {},
        files: [], // Pass empty files array since we're not uploading files here
        directImages: updatedImages // Use directImages to override the default image combination logic
      })).unwrap();
    } catch (err) {
      console.error('Failed to update images:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      await dispatch(updateReport({
        reportId: currentReport.id,
        reportData: {
          ...formData,
          // Remove images from reportData since we'll pass them via directImages
          images: undefined
        },
        files: [], // Pass empty files array since images are already uploaded and in formData
        directImages: formData.images // Pass images via directImages to avoid duplication
      })).unwrap();
      
      setSuccess(true);
      
      // Close after success message
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update report');
    } finally {
      setLoading(false);
    }
  };

  // Get available games from the gameNames data
  const availableGames = gameNames.map(game => game.name);

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-game-review-modal">
        <div className="modal-header">
          <h3>Edit Game Testing Report</h3>
          <div className="header-actions">
            {formData.selectedGames.length > 0 && (
              <button 
                type="button"
                className="bug-report-button"
                onClick={() => setShowBugReportForm(true)}
              >
                🐛 Report Bug
              </button>
            )}
            <button className="close-button" onClick={onHide}>&times;</button>
          </div>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="games-section-header">
                <h4>Selected Games</h4>
                {autoSaving && (
                  <span className="auto-save-indicator">
                    ⟳ Saving...
                  </span>
                )}
              </div>
              <div className="games-grid">
                {availableGames.map((game, index) => (
                  <div 
                    key={index} 
                    className={`game-item ${formData.selectedGames.includes(game) ? 'selected' : ''}`}
                    onClick={() => handleGameSelection(game)}
                  >
                    {game}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Show existing bug reports if any */}
            {currentReport?.bugReports && currentReport.bugReports.length > 0 && (
              <div className="form-section">
                <h4>Bug Reports ({currentReport.bugReports.length})</h4>
                <div className="bug-reports-summary">
                  {currentReport.bugReports.map((bug, index) => (
                    <div key={index} className="bug-item">
                      <span className={`bug-severity ${bug.sensitivity}`}>
                        {bug.sensitivity}
                      </span>
                      <span className="bug-title">{bug.title}</span>
                      <span className="bug-game">{bug.gameName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="form-section">
              <h4>Game Feedback</h4>
              
              <div className="form-group">
                <label htmlFor="overallExperience">Overall Experience</label>
                <textarea
                  id="overallExperience"
                  name="overallExperience"
                  value={formData.overallExperience}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe your overall experience with the games..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="levelsFeedback">Levels Feedback</label>
                <textarea
                  id="levelsFeedback"
                  name="levelsFeedback"
                  value={formData.levelsFeedback}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Provide feedback about the game levels..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="tutorialFeedback">Tutorial Feedback</label>
                <textarea
                  id="tutorialFeedback"
                  name="tutorialFeedback"
                  value={formData.tutorialFeedback}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Provide feedback about the tutorials..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="positiveAspects">Positive Aspects</label>
                <textarea
                  id="positiveAspects"
                  name="positiveAspects"
                  value={formData.positiveAspects}
                  onChange={handleChange}
                  rows={4}
                  placeholder="What aspects of the games worked well?"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="improvementSuggestions">Improvement Suggestions</label>
                <textarea
                  id="improvementSuggestions"
                  name="improvementSuggestions"
                  value={formData.improvementSuggestions}
                  onChange={handleChange}
                  rows={4}
                  placeholder="What suggestions do you have for improvements?"
                />
              </div>
            </div>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message">
                Report updated successfully!
              </div>
            )}
            
            {bugSubmitSuccess && (
              <div className="success-message">
                ✓ Bug report added successfully! Continue editing your report.
              </div>
            )}
          </form>
          
          {/* Image Upload Section - Moved to Bottom */}
          <div className="form-section image-upload-bottom-section">
            <h4>Screenshots & Images {formData.images && formData.images.length > 0 && `(${formData.images.length})`}</h4>
            
            {/* Image Preview Grid - Show existing images first */}
            {formData.images && formData.images.length > 0 && (
              <div className="images-preview-grid">
                {formData.images.map((image, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={image.url} alt={image.name} />
                    <div className="image-overlay">
                      <button
                        type="button"
                        className="remove-image-button"
                        onClick={() => removeImage(index)}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                    <div className="image-name">{image.name}</div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="image-upload-section">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="upload-images-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImages}
              >
                {uploadingImages ? (
                  <>
                    <span className="spinner"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    📷 Add Images
                  </>
                )}
              </button>
              
              {/* Upload Progress */}
              {Object.keys(uploadProgress).length > 0 && (
                <div className="upload-progress">
                  {Object.entries(uploadProgress).map(([fileName, progress]) => (
                    <div key={fileName} className="progress-item">
                      <span className="progress-filename">{fileName}</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <span className="progress-percentage">{Math.round(progress)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="cancel-button" 
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="button"
            className="save-button" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </div>
      
      {/* Bug Report Form Modal */}
      {showBugReportForm && (
        <BugReportForm
          reportId={currentReport?.id}
          selectedGames={formData.selectedGames}
          onClose={() => {
            setShowBugReportForm(false);
            setBugSubmitSuccess(true);
            // Clear the success message after 3 seconds
            setTimeout(() => setBugSubmitSuccess(false), 3000);
          }}
        />
      )}
    </div>
  );
};

export default EditGameReviewModal; 